import {
  Component,
  Input,
  forwardRef,
  ChangeDetectionStrategy,
  signal,
  computed,
  effect,
  Injector,
  OnInit,
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
  NgControl,
  ReactiveFormsModule,
  ValidationErrors,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { distinctUntilChanged, debounceTime } from 'rxjs';
import { getTakeUntilDestroyed } from '../../../../core/helpers/take-until-destroy.helper';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-input-field',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslatePipe],
  templateUrl: './input-field.component.html',
  styleUrls: ['./input-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputFieldComponent),
      multi: true,
    },
  ],
})
export class InputFieldComponent implements ControlValueAccessor, OnInit {
  private _label = signal('');
  private _placeholder = signal('');
  private _type = signal<'text' | 'email' | 'password' | 'number' | 'date'>(
    'text'
  );
  private _inputType = signal<'input' | 'textarea' | 'select' | 'datepicker'>(
    'input'
  );
  private _options = signal<Array<{ label: string; value: string | number }>>(
    []
  );
  private _prefixIcon = signal('');
  private _suffixIcon = signal('');
  private _disabled = signal(false);
  private _validationErrors = signal<Record<string, string>>({});

  private _showPwdComplexity = signal<boolean>(false);
  private _showPasswordToggle = signal(true);
  private _autocomplete = signal<string>('');
  private _pwdErrors = signal<string[]>([
    'minLength',
    'digit',
    'specialChar',
    'upperCase',
  ]);

  private _complexity1 = signal<boolean>(false);
  private _complexity2 = signal<boolean>(false);
  private _complexity3 = signal<boolean>(false);
  private _complexity4 = signal<boolean>(false);

  private takeUntilDestroyed = getTakeUntilDestroyed();

  @Input() set label(v: string) {
    this._label.set(v);
  }
  @Input() set showPwdComplexity(v: boolean) {
    this._showPwdComplexity.set(v);
  }
  @Input() set showPasswordToggle(v: boolean) {
    this._showPasswordToggle.set(!!v);
  }
  @Input() set autocomplete(v: string) {
    this._autocomplete.set(v ?? '');
  }
  @Input() set placeholder(v: string) {
    this._placeholder.set(v);
  }
  @Input() set type(v: 'text' | 'email' | 'password' | 'number' | 'date') {
    this._type.set(v);
  }
  @Input() set inputType(v: 'input' | 'textarea' | 'select' | 'datepicker') {
    this._inputType.set(v);
  }
  @Input() set options(v: Array<{ label: string; value: string | number }>) {
    this._options.set(v);
  }
  @Input() set prefixIcon(v: string) {
    this._prefixIcon.set(v);
  }
  @Input() set suffixIcon(v: string) {
    this._suffixIcon.set(v);
  }
  @Input() set disabled(v: boolean) {
    this._disabled.set(v);
  }
  @Input() set validationErrors(v: Record<string, string>) {
    this._validationErrors.set(v || {});
  }
  @Input() set selectedValue(v: any) {
    if (v !== undefined && v !== null) {
      this.value.set(v);
    }
  }

  value = signal<any>('');
  isFocused = signal(false);
  passwordVisible = signal(false);

  private onChange = (_: any) => {};
  private onTouched = () => {};
  private ngControl: NgControl | null = null;

  constructor(private injector: Injector) {
    effect(() => {
      if (this._disabled()) this.setDisabledState(true);
    });
  }

  ngOnInit(): void {
    try {
      // Only retrieve NgControl here to avoid circular dependency
      this.ngControl = this.injector.get(NgControl, null);
      if (this.ngControl) {
        this.ngControl.valueAccessor = this;
      }
    } catch (err) {
      this.ngControl = null;
    }
  }

  ngAfterViewInit() {
    this.watchPassword();
  }

  writeValue(value: any): void {
    this.value.set(value);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled.set(isDisabled);
  }

  onInputChange(event: Event) {
    const value = (
      event.target as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    ).value;
    this.value.set(value);
    this.onChange(value);
  }

  onFocus() {
    this.isFocused.set(true);
  }

  onBlur() {
    this.isFocused.set(false);
    this.onTouched();
  }

  togglePasswordVisibility(): void {
    this.passwordVisible.update((visible) => !visible);
  }

  onPasswordToggleMouseDown(event: MouseEvent): void {
    event.preventDefault();
  }

  get control() {
    return this.ngControl?.control;
  }

  get showError(): boolean {
    const control = this.control;
    return !!control && control.invalid && control.touched;
  }

  get showSuccess(): boolean {
    const control = this.control;
    if (!control || !control.valid || !control.touched || this.showError) {
      return false;
    }

    const current = this.value();
    return current !== '' && current !== null && current !== undefined;
  }

  get firstErrorMessage(): string | null {
    const control = this.control;
    if (!control?.errors) return null;
    const key = Object.keys(control.errors)[0];
    return this._validationErrors()?.[key];
  }

  get errors(): ValidationErrors | undefined | null {
    const control = this.control;

    return control?.errors;
  }

  private watchPassword(): void {
    if (!this.complexitySig()) return;
    this.control!.valueChanges.pipe(
      distinctUntilChanged(),
      debounceTime(300),
      this.takeUntilDestroyed()
    ).subscribe((pass) => {
      const step1 = /.{8,}/;
      const step2 = /[A-Z]/;
      const step3 = /[0-9]/;
      const step4 = /[!@#$%^&*(),.?":{}|<>]/;

      this._complexity1.set(false);
      this._complexity2.set(false);
      this._complexity3.set(false);
      this._complexity4.set(false);

      if (step1.test(pass)) {
        this._complexity1.set(true);
      }

      if (
        (step1.test(pass) && step2.test(pass)) ||
        (step1.test(pass) && step3.test(pass)) ||
        (step1.test(pass) && step4.test(pass))
      ) {
        this._complexity2.set(true);
      }

      if (
        (step1.test(pass) && step2.test(pass) && step3.test(pass)) ||
        (step1.test(pass) && step3.test(pass) && step4.test(pass)) ||
        (step1.test(pass) && step2.test(pass) && step4.test(pass))
      ) {
        this._complexity2.set(true);
        this._complexity3.set(true);
      }

      if (
        step1.test(pass) &&
        step2.test(pass) &&
        step3.test(pass) &&
        step4.test(pass)
      ) {
        this._complexity4.set(true);
      }
    });
  }

  // Expose signals to template
  labelSig = computed(() => this._label());
  typeSig = computed(() => this._type());
  displayTypeSig = computed(() => {
    if (this._type() !== 'password') return this._type();
    return this.passwordVisible() ? 'text' : 'password';
  });
  showPasswordToggleSig = computed(() => this._showPasswordToggle());
  showPasswordToggleButtonSig = computed(
    () =>
      this._showPasswordToggle() &&
      this._type() === 'password' &&
      this._inputType() === 'input',
  );
  inputTypeSig = computed(() => this._inputType());
  placeholderSig = computed(() => this._placeholder());
  prefixIconSig = computed(() => this._prefixIcon());
  suffixIconSig = computed(() => this._suffixIcon());
  disabledSig = computed(() => this._disabled());
  optionsSig = computed(() => this._options());
  autocompleteSig = computed(() => {
    const explicit = this._autocomplete().trim();
    if (explicit) {
      return explicit;
    }
    switch (this._type()) {
      case 'password':
        return 'current-password';
      case 'email':
        return 'email';
      default:
        return '';
    }
  });

  complexitySig = computed(() => this._showPwdComplexity());

  complexity1Sig = computed(() => this._complexity1());
  complexity2Sig = computed(() => this._complexity2());
  complexity3Sig = computed(() => this._complexity3());
  complexity4Sig = computed(() => this._complexity4());
  pwdErrors = computed(() => this._pwdErrors());
}
